import { spammerLabel } from './spammer';

const MESSAGE_PREVIEW_EXCLUDED_PSEUDOTYPES = new Set(['skeleton', 'zombie', 'optimistic']);
// Typed messages are system/event rows that don't belong in the rail preview — except
// widget announcements, which we surface as "<actor> <verb> <subject>" like a chat line.
const MESSAGE_PREVIEW_ALLOWED_TYPES = new Set(['widget_announcement']);

export function isMessagePreviewCandidate(message) {
  if (!message?.sort_order) return false;
  if (message.type != null && !MESSAGE_PREVIEW_ALLOWED_TYPES.has(message.type)) return false;
  if (MESSAGE_PREVIEW_EXCLUDED_PSEUDOTYPES.has(message.pseudotype)) return false;
  return true;
}

export function normalizeMessagePreviewText(text) {
  const previewText = (text || '').replace(/\s+/g, ' ').trim();
  return previewText || null;
}

export function getMessagePreviewAuthorName(profile) {
  return normalizeMessagePreviewText(profile?.full_name);
}

function getAttachmentPreviewText(message) {
  const attachmentCount = message.attachment_count ?? (Array.isArray(message.attachments) ? message.attachments.length : 0);
  if (attachmentCount <= 0) return null;
  return `${attachmentCount} attachment${attachmentCount === 1 ? '' : 's'}`;
}

// Pull a human-readable label out of the resolved snapshot map (the shape produced by
// buildMessageEntitySnapshots / loadMessageEntitySnapshots). Returns null when the entity
// isn't represented in the map so the caller can fall through to its generic labels.
function getResolvedEntityLabel(entity, entitySnapshots) {
  const dataType = entity.dataset.type;

  // @everyone is stored as a spammer entity, not a profile snapshot.
  if (dataType === 'spammer') return `@${spammerLabel(entity.dataset.target)}`;
  if (!entitySnapshots) return null;

  if (dataType === 'profile') {
    const snapshot = entitySnapshots.profile?.[entity.dataset.target];
    return snapshot?.full_name ? `@${snapshot.full_name}` : null;
  }
  if (dataType === 'channel') {
    const snapshot = entitySnapshots.channel?.[entity.dataset.target];
    return snapshot?.name ? `#${snapshot.name}` : null;
  }
  if (dataType === 'emoji') {
    const snapshot = entitySnapshots.emoji?.[entity.dataset.id];
    if (snapshot?.native) return snapshot.native;
    if (snapshot?.shortcode) return `:${snapshot.shortcode}:`;
    return null;
  }
  return null;
}

function getEntityPreviewText(entity, entitySnapshots) {
  const resolved = getResolvedEntityLabel(entity, entitySnapshots);
  if (resolved) return resolved;

  // Fallbacks for entities whose snapshot wasn't passed in or hasn't synced yet — the
  // stored richdown has no inline name, so without snapshots we end up at the generic label.
  const dataType = entity.dataset.type;
  const imageAlt = entity.querySelector('img')?.getAttribute('alt');
  const label = entity.dataset.name || entity.textContent || imageAlt;
  if (label) return label;
  if (dataType === 'emoji') return 'Emoji';
  if (dataType === 'channel') return 'Channel';
  if (dataType === 'profile' || dataType === 'spammer') return 'Mention';
  return '';
}

// Cheap, deterministic signature of just the labels we'd substitute. Resolved text depends
// on (html, snapshots), so the cache has to key on both — otherwise two messages with the
// same html but different mentioned profiles would alias to one entry.
function fingerprintEntitySnapshots(entitySnapshots) {
  if (!entitySnapshots) return '';
  const parts = [];
  for (const kind of ['profile', 'channel', 'emoji']) {
    const map = entitySnapshots[kind];
    if (!map) continue;
    for (const id of Object.keys(map).sort()) {
      const snapshot = map[id];
      const label = kind === 'emoji' ? snapshot?.native || snapshot?.shortcode || '' : snapshot?.full_name || snapshot?.name || '';
      parts.push(`${kind[0]}:${id}=${label}`);
    }
  }
  return parts.join('|');
}

// Module-level LRU cache for sanitized preview text.
//
// `useLiveQuery` re-runs the workspace-rail preview hook on every write to the rail projection
// or db.profiles, which means an incoming message in one channel re-parses every visible
// workspace's latest message through DOMParser. Most of those re-parses produce identical
// output because the underlying HTML and resolved entity labels didn't change.
//
// Keying by html + snapshot fingerprint makes hits content-addressed: identical richdown
// across workspaces shares an entry, and any edit (text or a renamed profile/channel)
// invalidates naturally by producing a new key. Map iteration order is insertion order,
// so we evict the oldest entry once we exceed the cap — bounded memory, no extra bookkeeping.
const RICHDOWN_PREVIEW_CACHE = new Map();
const RICHDOWN_PREVIEW_CACHE_MAX = 200;

function getRichdownPreviewText(html, entitySnapshots) {
  if (!html) return null;

  // U+0001 is a non-printable separator that can't appear in real richdown, so it cleanly
  // splits the html half of the key from the snapshot fingerprint half.
  const cacheKey = `${html}${fingerprintEntitySnapshots(entitySnapshots)}`;

  const cached = RICHDOWN_PREVIEW_CACHE.get(cacheKey);
  if (cached !== undefined) {
    // Refresh recency so frequently-seen entries survive eviction.
    RICHDOWN_PREVIEW_CACHE.delete(cacheKey);
    RICHDOWN_PREVIEW_CACHE.set(cacheKey, cached);
    return cached;
  }

  const doc = new DOMParser().parseFromString(html, 'text/html');

  doc.body.querySelectorAll('entity').forEach(function replaceEntityWithText(entity) {
    const label = getEntityPreviewText(entity, entitySnapshots);
    entity.replaceWith(doc.createTextNode(label ? ` ${label} ` : ' '));
  });

  const result = normalizeMessagePreviewText(doc.body.textContent);

  RICHDOWN_PREVIEW_CACHE.set(cacheKey, result);
  if (RICHDOWN_PREVIEW_CACHE.size > RICHDOWN_PREVIEW_CACHE_MAX) {
    RICHDOWN_PREVIEW_CACHE.delete(RICHDOWN_PREVIEW_CACHE.keys().next().value);
  }
  return result;
}

export function getMessagePreviewText(message, entitySnapshots) {
  if (!message) return null;
  if (message.contains_only_attachments) return getAttachmentPreviewText(message);

  const richdownPreview = getRichdownPreviewText(message.text, entitySnapshots);
  if (richdownPreview) return richdownPreview;

  return getAttachmentPreviewText(message);
}

const WIDGET_ANNOUNCEMENT_GENERIC_LABELS = {
  link: 'Link',
  todo: 'To-do',
};

function isGenericWidgetSubjectLabel(kind, label) {
  return label === WIDGET_ANNOUNCEMENT_GENERIC_LABELS[kind];
}

// Resolve the human label for a widget announcement's subject. Shared with the in-channel
// WidgetAnnouncement component so the rail preview reads identically: todo/link rows fall
// back to the body/title payload when the stored label is empty or the generic kind name.
export function displayWidgetSubjectLabel(subject) {
  const kind = subject?.kind;
  const payload = subject?.payload || {};
  const label = normalizeMessagePreviewText(subject?.label) || '';

  if (kind === 'todo' && (!label || isGenericWidgetSubjectLabel(kind, label))) {
    return normalizeMessagePreviewText(payload.body) || label || 'item';
  }
  if (kind === 'link' && (!label || isGenericWidgetSubjectLabel(kind, label))) {
    return normalizeMessagePreviewText(payload.title) || label || 'item';
  }

  return label || 'item';
}

// Build the rail-preview pieces for a widget announcement: a bold actor name plus the
// "<verb> <subject>" remainder (e.g. "Allan" / "added a task: Migrate to new domain").
export function getWidgetAnnouncementPreview(message) {
  const data = message?.data || {};
  const actor = Array.isArray(message?.actors) ? message.actors[0] : null;
  const authorName = normalizeMessagePreviewText(actor?.full_name) || 'Someone';
  const verb = normalizeMessagePreviewText(data.verb) || 'updated';
  const subjectLabel = displayWidgetSubjectLabel(data.subject || {});
  const text = normalizeMessagePreviewText(`${verb} ${subjectLabel}`);

  return { authorName, text };
}
